import React from "react";
import { Skeleton } from "@mui/material";
import { GfImage } from "@swagger/typescript-fetch-goflyer";
import { ThumbnailContainer, ThumbnailWrapper } from "../styled";
import { THUMBNAIL_BAR_CONSTANTS } from "../constants";

interface ThumbnailBarProps {
  gfImages: GfImage[];
  currentPage: number;
  isLoading: boolean;
  onPageClick: (flyerIndex: number) => void;
}

/**
 * Horizontal thumbnail bar displaying all flyer pages with navigation capability.
 *
 * This component renders a scrollable row of thumbnail images representing each page
 * in the flyer. Users can click any thumbnail to navigate to that page. The currently
 * active page is visually highlighted. While loading, skeleton placeholders are shown.
 * Images are automatically sorted by their flyerIndex and filtered to exclude any
 * images without valid index or source data.
 *
 * @param props - Component properties
 * @param props.gfImages - Array of image objects containing flyer page images
 * @param props.currentPage - Zero-based index of the currently displayed page
 * @param props.isLoading - Whether images are currently being loaded
 * @param props.onPageClick - Callback invoked when a thumbnail is clicked, receives zero-based page index
 */
export const ThumbnailBar: React.FC<ThumbnailBarProps> = ({
  gfImages,
  currentPage,
  isLoading,
  onPageClick,
}) => {
  const sortedImages = [...gfImages]
    .filter((img) => img.flyerIndex !== undefined && img.src)
    .sort((a, b) => (a.flyerIndex || 0) - (b.flyerIndex || 0));

  if (isLoading) {
    return (
      <ThumbnailContainer data-testid="flyer-coordinate-thumbnail-bar-loading">
        {[...Array(THUMBNAIL_BAR_CONSTANTS.SKELETON_COUNT)].map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width={THUMBNAIL_BAR_CONSTANTS.SKELETON_WIDTH}
            height={THUMBNAIL_BAR_CONSTANTS.SKELETON_HEIGHT}
            sx={{
              borderRadius: THUMBNAIL_BAR_CONSTANTS.SKELETON_BORDER_RADIUS,
            }}
          />
        ))}
      </ThumbnailContainer>
    );
  }

  if (sortedImages.length === 0) {
    return null;
  }

  return (
    <ThumbnailContainer data-testid="flyer-coordinate-thumbnail-bar">
      {sortedImages.map((image, index) => {
        const flyerIndex = image.flyerIndex || 0;
        const isActive = index === currentPage;

        return (
          <ThumbnailWrapper
            key={image.id}
            isActive={isActive}
            onClick={() => onPageClick(index)}
            data-testid={`flyer-coordinate-thumbnail-item-${index}`}
          >
            <img
              src={image.src}
              alt={image.alt || `Page ${flyerIndex}`}
              loading="lazy"
            />
          </ThumbnailWrapper>
        );
      })}
    </ThumbnailContainer>
  );
};
